import {
  BREEK,
  CLOTHING_GARMENTS,
  COAT_PANT_PANT,
  COAT_VEST_PANT_PANT,
  COMPOSED_GARMENTS,
  SHIRT,
  SWACKET,
  uniqArray,
} from '@trinity/utils'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { MenuItem, Stack, TextField, UnfoldMore } from '../components'

interface UseGarmentTypeFiltersProps {
  fabrics: TrinityAPI.FabricImageType[]
}

/**
 * Garment type + base model filters for Styleflow and Fabric Explorer garment views.
 *
 * Selection rules (post-fix):
 * - `model` is updated only from user actions, `fabrics` changes, or invalid-model recovery, not from a
 *   continuous “sync URL into state” loop. That loop used to fight `setModel` when search params lagged
 *   one frame behind the controlled select, snapping the Model dropdown back to the previous value.
 * - `currentFabrics` is derived in a dedicated effect from `fabrics` + `model` so we do not duplicate
 *   that logic inside `handleModelChange`.
 */
export function useGarmentTypeFilters({ fabrics }: UseGarmentTypeFiltersProps) {
  const [params, setParams] = useSearchParams()
  const [garmentType, setGarmentType] = useState<number | null>(() => fabrics[0]?.garmentType?.bitmask ?? null)
  const validGarmentTypes = getValidGarmentTypes(fabrics)
  const [models, setModels] = useState(() => getModels(fabrics))

  const initialModel = initialModelFromFabricsAndUrl(fabrics, params.get('model'))
  const [model, setModel] = useState(initialModel)
  const [currentFabrics, setCurrentFabrics] = useState(() => getDefaultFabrics(fabrics, initialModel || undefined))
  const [garmentTypeChanged, setGarmentTypeChanged] = useState(false)

  useEffect(() => {
    const newGarmentType = fabrics[0]?.garmentType?.bitmask ?? null
    setGarmentType(newGarmentType)
    const newModels = getModels(fabrics)
    setModels(newModels)
    setGarmentTypeChanged(false)
    setModel(prev => (newModels.includes(prev ?? '') ? prev : (newModels?.[0] ?? '')))
  }, [fabrics])

  useEffect(() => {
    setCurrentFabrics(getDefaultFabrics(fabrics, model || undefined))
  }, [fabrics, model])

  const handleGarmentChange = useCallback(
    (newGarment?: string) => {
      setParams(params => {
        params.set('garmentType', newGarment ?? '')
        params.delete('model')
        return params
      })
      setGarmentTypeChanged(true)
    },
    [setParams],
  )

  const handleModelChange = useCallback(
    (newModel?: string) => {
      if (model !== newModel) {
        setModel(newModel ?? '')
        setParams(params => {
          if (newModel) {
            params.set('model', newModel)
          } else {
            params.delete('model')
          }
          return params
        })
      }
    },
    [model, setParams],
  )

  useEffect(() => {
    if (!model || !models.includes(model)) {
      handleModelChange(models[0])
    }
  }, [handleModelChange, model, models])

  return {
    currentFabrics,
    garmentType,
    validGarmentTypes,
    models,
    model,
    GarmentFilters: () => (
      <GarmentFilters
        garmentTypes={validGarmentTypes.map(gt => ({ bitmask: gt.bitmask, value: gt.alternateName ?? gt.formalName }))}
        garmentType={garmentType}
        model={model ?? ''}
        models={models}
        disable={garmentTypeChanged}
        handleGarmentChange={handleGarmentChange}
        handleModelChange={handleModelChange}
      />
    ),
  }
}

const getModels = (fabrics: TrinityAPI.FabricImageType[]) =>
  uniqArray(fabrics.map(fabric => fabric.baseModel.description))

function initialModelFromFabricsAndUrl(fabrics: TrinityAPI.FabricImageType[], modelParam: string | null) {
  const list = getModels(fabrics)
  if (modelParam && list.includes(modelParam)) return modelParam
  return list[0] ?? ''
}

const getDefaultFabrics = (fabrics: TrinityAPI.FabricImageType[], baseModel?: string) => {
  let model = baseModel

  if (!model) {
    const models = getModels(fabrics)
    model = models[0]
  }

  return fabrics.filter(fabric => fabric.baseModel.description === model)
}

const getValidGarmentTypes = (fabrics: TrinityAPI.FabricImageType[]): TrinityAPI.GarmentTypesType[] => {
  const ignoredGarmentTypes: TrinityAPI.GarmentTypesType[] = [COAT_PANT_PANT, COAT_VEST_PANT_PANT, BREEK, SWACKET]
  const currentGarmentType = fabrics[0]?.garmentType
  if (!currentGarmentType) return []
  const baseGarmentTypes = currentGarmentType === SHIRT ? [SHIRT] : [...CLOTHING_GARMENTS, ...COMPOSED_GARMENTS]
  return baseGarmentTypes.filter(type => !ignoredGarmentTypes.includes(type))
}

interface GarmentFiltersProps {
  garmentType: number | null | undefined
  garmentTypes: { bitmask: number; value: string }[]
  model: string
  models: string[]
  disable: boolean
  handleGarmentChange: (garment: string | undefined) => void
  handleModelChange: (model: string | undefined) => void
}

const GarmentFilters = ({
  garmentType,
  garmentTypes,
  model,
  models,
  disable,
  handleGarmentChange,
  handleModelChange,
}: GarmentFiltersProps) => {
  return (
    <Stack direction={{ mobile: 'column', laptop: 'row' }} spacing={{ mobile: 2, laptop: 4 }}>
      <TextField
        select
        size='small'
        name='garmentType'
        label='Garment Type'
        slotProps={{ select: { IconComponent: UnfoldMore } }}
        value={garmentType}
        sx={{ width: 200 }}
        onChange={e => handleGarmentChange(e.target.value)}
        disabled={disable}
      >
        {garmentTypes.map(type => (
          <MenuItem key={type.bitmask} value={type.bitmask}>
            {type.value}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size='small'
        name='model'
        label='Model'
        value={model}
        sx={{ width: 200 }}
        onChange={e => handleModelChange(e.target.value)}
        disabled={disable}
      >
        {models.map(model => (
          <MenuItem key={model} value={model}>
            {model}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  )
}
